'use client'

import { useEffect, useState, useRef, useCallback } from 'react'
import { useApi } from '@/contexts/ApiContext'
import { useRouter, usePathname } from 'next/navigation'
import { getCookie, setCookie } from 'cookies-next'
import { useLaunchDarklyFlag } from '@/lib/launchDarklyFlagEvaluator'
import { useUserContext } from '@/contexts/UserContext'
import { getRequestOrigin } from '@/lib/getRequestOrigin'
import { trackEvent } from '@nile/mantine-theme/analytics'

const GENAI_INGRESS_KEY = 'genai-ingress-source'
const ONBOARDING_DISMISSED_KEY = 'genai-onboarding-dismissed'

export type GenAiIngressSource =
    | 'landing_page_hero'
    | 'landing_page_how_it_works'
    | 'landing_page_faq'
    | 'fab_store'
    | 'fab_lab_results'
    | 'fab_account'
    | 'homepage_banner'
    | 'homepage_card'
    | 'dashboard_action_pill'
    | 'dashboard_link_row'
    | 'hamburger_menu'
    | 'category_page_hero'

interface GeneralAiStatusResult {
    hasExistingConversation: boolean
    isLoading: boolean
    isFromGenAi: boolean
    setGenAiIngress: () => void
    clearGenAiIngress: () => void
    navigateToGeneralAi: (ingress?: GenAiIngressSource) => Promise<void>
}

interface SessionExistsResponse {
    exists: boolean
    messages_count: number
}

/**
 * Hook to check if user has an existing General AI conversation
 * Returns true if user has more than 1 message (indicating they've interacted with AI)
 * Also tracks if user entered from gen AI (e.g., clicked "get started" from gen AI)
 */
export function useGeneralAiStatus(): GeneralAiStatusResult {
    const { apiGet } = useApi()
    const router = useRouter()
    const pathname = usePathname()
    const { user } = useUserContext()
    const hasFetchedRef = useRef(false)
    const [status, setStatus] = useState<{ hasExistingConversation: boolean; isLoading: boolean }>({
        hasExistingConversation: false,
        isLoading: true,
    })
    const [isFromGenAi, setIsFromGenAi] = useState(false)
    const { flagEnabled: prewarmEnabled } = useLaunchDarklyFlag('gen-ai-prewarm-copilot')

    // Callers can fire navigateToGeneralAi before the session-status fetch
    // resolves; keep a ref so the callback always reads the latest values
    // rather than the ones captured when the click handler was created.
    const statusRef = useRef(status)
    statusRef.current = status
    const statusReadyResolversRef = useRef<Array<() => void>>([])

    useEffect(() => {
        if (status.isLoading) return
        statusReadyResolversRef.current.forEach((resolve) => resolve())
        statusReadyResolversRef.current = []
    }, [status.isLoading])

    // Resolves once hasExistingConversation reflects the real session state,
    // so a click that lands mid-fetch waits instead of routing off stale data.
    const waitForStatusReady = useCallback((): Promise<void> => {
        if (!statusRef.current.isLoading) return Promise.resolve()
        return new Promise((resolve) => {
            statusReadyResolversRef.current.push(resolve)
        })
    }, [])

    // Helper function to check if user is authenticated
    // Uses UserContext instead of cookies because accessToken is httpOnly
    const isAuthenticated = (): boolean => {
        return user !== null
    }

    // Check ingress on mount and auto-detect Gen AI traffic
    useEffect(() => {
        // First check if flag is already set
        const ingressValue = sessionStorage.getItem(GENAI_INGRESS_KEY)
        if (ingressValue === 'true') {
            setIsFromGenAi(true)
            return
        }

        // Auto-detect Gen AI traffic from redirect_url cookie
        const redirectUrl = getCookie('redirect_url')
        if (redirectUrl && redirectUrl.includes('/ai/')) {
            sessionStorage.setItem(GENAI_INGRESS_KEY, 'true')
            setIsFromGenAi(true)
            return
        }

        // Also check URL params for Gen AI traffic
        if (typeof window !== 'undefined') {
            const urlParams = new URLSearchParams(window.location.search)
            const signupSource = urlParams.get('signup-source')
            if (signupSource === 'general-ai') {
                sessionStorage.setItem(GENAI_INGRESS_KEY, 'true')
                setIsFromGenAi(true)
            }
        }
    }, [])

    useEffect(() => {
        // Only fetch once per authenticated user
        if (hasFetchedRef.current) return

        // Skip API call if user is not authenticated
        // Don't set hasFetchedRef here - allow re-check when user loads
        if (!user) {
            setStatus({
                hasExistingConversation: false,
                isLoading: false,
            })
            return
        }

        // Set loading to true before starting the API call
        // This prevents a race where the chat page sees a stale isLoading: false
        setStatus((prev) => ({ ...prev, isLoading: true }))

        const checkConversationStatus = async () => {
            hasFetchedRef.current = true

            try {
                const response = await apiGet<SessionExistsResponse>('/external/copilot/session_exists')
                const messageCount = response?.messages_count || 0
                setStatus({
                    hasExistingConversation: messageCount > 1,
                    isLoading: false,
                })
            } catch (error) {
                console.error('Error checking session status:', error)
                setStatus({
                    hasExistingConversation: false,
                    isLoading: false,
                })
            }
        }

        checkConversationStatus()
    }, [apiGet, user])

    const setGenAiIngress = () => {
        sessionStorage.setItem(GENAI_INGRESS_KEY, 'true')
        setIsFromGenAi(true)
    }

    const clearGenAiIngress = () => {
        sessionStorage.removeItem(GENAI_INGRESS_KEY)
        setIsFromGenAi(false)
    }

    const navigateToGeneralAi = useCallback(
        async (ingress?: GenAiIngressSource) => {
            // Track ingress event if source is provided
            if (ingress) {
                trackEvent('clicked_genai_ingress', { ingress })
            }

            // Set ingress flag to track Gen AI entry
            setGenAiIngress()

            // Check if user is authenticated - redirect appropriately if not
            if (!isAuthenticated()) {
                // If already on the /ai page, redirect to signup
                const isOnAiPage = pathname === '/ai'
                if (isOnAiPage) {
                    // Set redirect_url cookie so user returns to AI page after signup
                    if (typeof window !== 'undefined') {
                        const aiUrl = `${getRequestOrigin()}/ai/onboarding`
                        setCookie('redirect_url', aiUrl, {
                            path: '/',
                            maxAge: 1800, // 30 minutes
                            domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN,
                        })
                    }
                    // Redirect to signup page
                    router.push('/get-started')
                } else {
                    // Not on /ai page, redirect to AI page (which will handle auth)
                    router.push('/ai')
                }
                return
            }

            // The session-status fetch may still be in flight; wait for it so the
            // decision below reflects the real conversation state, not the
            // still-loading default.
            await waitForStatusReady()
            const { hasExistingConversation } = statusRef.current

            // Check if user has dismissed onboarding
            const hasUserDismissedOnboarding = localStorage.getItem(ONBOARDING_DISMISSED_KEY) === 'true'

            if (hasExistingConversation) {
                router.push('/ai/chat')
                return
            }

            // User dismissed onboarding but no conversation yet
            if (hasUserDismissedOnboarding) {
                const destination = prewarmEnabled && !hasExistingConversation ? '/ai/health-records' : '/ai/chat'
                router.push(destination)
                return
            }

            router.push('/ai/onboarding')
        },
        [pathname, prewarmEnabled, router, waitForStatusReady],
    )

    return {
        ...status,
        isFromGenAi,
        setGenAiIngress,
        clearGenAiIngress,
        navigateToGeneralAi,
    }
}
